import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import QRCode from 'qrcode';
import {EhiExportOutputType, VaultEhiExportResponse} from '@shared-library';
import {RecaptchaService} from '../../services/recaptcha.service';
import {FastenService} from '../../services/fasten.service';
import {firstValueFrom, Subscription, switchMap, timer} from 'rxjs';

type ExportResourceOption = {
  label: string;
  resourceType: string;
  selected: boolean;
};

type ExportAccessDurationOption = {
  id: '1-day' | '1-week' | '1-month' | '6-months' | '1-year';
  label: string;
};

type ExportModalState = 'closed' | 'config' | 'loading' | 'complete';
type ExportLoadingPhase = 'retrieving' | 'qr-generation';
type ExportRecaptchaState = 'pending' | 'verifying' | 'error';

@Component({
  selector: 'app-account-export-modal',
  templateUrl: './account-export-modal.component.html',
  styleUrls: ['./account-export-modal.component.css'],
  standalone: false
})
export class AccountExportModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() exportType?: EhiExportOutputType;
  @Input() accountDisplayName = 'Connected institution';
  @Input() orgConnectionId = '';
  @Output() closed = new EventEmitter<void>();

  exportModalState: ExportModalState = 'closed';
  exportLoadingPhase: ExportLoadingPhase = 'retrieving';
  exportResourceOptions: ExportResourceOption[] = [];
  exportDurationOptions: ExportAccessDurationOption[] = [
    { id: '1-day', label: '1 day' },
    { id: '1-week', label: '1 week' },
    { id: '1-month', label: '1 month' },
    { id: '6-months', label: '6 months' },
    { id: '1-year', label: '1 year' },
  ];
  selectedExportDurationId: ExportAccessDurationOption['id'] = '1-week';
  lastStatusCheckLabel = '';
  retrievalCheckCount = 0;
  downloadUrl = '';
  resultDocumentUrl = '';
  qrCodeDataUrl = '';
  smartHealthLinkUrl: string | null = null;
  cardTitle = 'SMART Health Link';
  patientName = '';
  patientDob = '';
  verifiedDate: string | null = null;
  recaptchaState: ExportRecaptchaState = 'pending';
  recaptchaErrorMessage = '';
  recaptchaToken = '';
  exportRequestErrorMessage = '';
  private retrievalPollingSubscription: Subscription | null = null;

  constructor(
    private recaptchaService: RecaptchaService,
    private fastenService: FastenService,
  ) {
    this.resetExportConfiguration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['exportType']) {
      if (this.isOpen && this.exportType) {
        this.prepareForOpen();
      } else {
        this.resetClosedState();
      }
    }
  }

  ngOnDestroy(): void {
    this.clearExportTimers();
  }

  // Config state: selection and copy shown before the export request is submitted.
  get selectedResourceCount(): number {
    return this.exportResourceOptions.filter((option) => option.selected).length;
  }

  get selectedResourceLabels(): string[] {
    return this.exportResourceOptions
      .filter((option) => option.selected)
      .map((option) => option.label);
  }

  get selectedResourceListLabel(): string {
    const labels = this.selectedResourceLabels;

    if (labels.length === 0) {
      return 'No categories selected';
    }

    if (labels.length === 1) {
      return labels[0];
    }

    return new Intl.ListFormat('en-US', {
      style: 'long',
      type: 'conjunction',
    }).format(labels);
  }

  get areAllResourcesSelected(): boolean {
    return this.exportResourceOptions.length > 0 && this.exportResourceOptions.every((option) => option.selected);
  }

  get requiresTimeboxedAccess(): boolean {
    return !!this.exportType && !(this.exportType === 'pdf');
  }

  get exportModalTitle(): string {
    return this.getExportTypeLabel();
  }

  // Loading state: progress copy changes as the modal simulates backend retrieval work.
  get exportLoadingTitle(): string {
    if (this.exportLoadingPhase === 'qr-generation') {
      return 'Generating QR code';
    }

    return 'Preparing export';
  }

  get exportLoadingMessage(): string {
    if (this.exportLoadingPhase === 'qr-generation') {
      return 'Creating your QR code and packaging the final share experience.';
    }

    return 'Retrieving medical record data and checking export status every 5 seconds.';
  }

  // Complete state: result messaging depends on whether the export resolves to a file or shareable link.
  get exportResultTitle(): string {
    if (this.exportType === 'pdf') {
      return 'Download ready';
    }

    if (this.exportType === 'cms_patient_shared_health_document') {
      return 'Document ready';
    }

    if (this.exportType === 'fhir_bundle') {
      return 'Link ready';
    }

    return 'Export ready';
  }

  get exportResultSummary(): string {
    if (this.exportType === 'pdf') {
      return `Your PDF export includes ${this.selectedResourceListLabel}.`;
    }

    return `Your share package includes ${this.selectedResourceCount} selected record categories and will stay active for ${this.getSelectedDurationLabel().toLowerCase()}.`;
  }

  get exportResultPrimaryActionLabel(): string {
    if (this.exportType === 'fhir_bundle') {
      return 'Open SMART Health Link';
    }

    return 'Open Document';
  }

  get exportQrCodeDescription(): string {
    if (this.exportType === 'fhir_bundle') {
      return `Scan this QR code to open ${this.cardTitle}. Access remains active for ${this.selectedDurationLabel.toLowerCase()}.`;
    }

    return `Scan this QR code to open the shared export. Access remains active for ${this.selectedDurationLabel.toLowerCase()}.`;
  }

  get selectedDurationLabel(): string {
    return this.getSelectedDurationLabel();
  }

  get canCloseFromBackdrop(): boolean {
    return this.exportModalState === 'config';
  }

  get isRecaptchaVerifying(): boolean {
    return this.recaptchaState === 'verifying';
  }

  get shouldShowRecaptchaPanel(): boolean {
    return this.recaptchaState === 'error' || !!this.recaptchaErrorMessage;
  }

  get recaptchaStatusLabel(): string {
    if (this.recaptchaState === 'verifying') {
      return 'Running';
    }

    if (this.recaptchaState === 'error') {
      return 'Retry needed';
    }

    return 'Pending';
  }

  get recaptchaStatusClass(): string {
    return this.recaptchaState === 'error' ? 'is-warning' : 'is-active';
  }

  get confirmButtonLabel(): string {
    return this.isRecaptchaVerifying ? 'Verifying...' : 'Confirm';
  }

  // Cross-state close affordances: config can close immediately, later states require confirmation.
  get closeConfirmationMessage(): string {
    if (this.exportModalState === 'loading') {
      return 'Are you sure you want to close this export? Your export is still in progress.';
    }

    if (this.exportModalState === 'complete') {
      return 'Are you sure you want to close this export? Make sure you have saved anything you need first.';
    }

    return '';
  }

  // Closed state: only the configuration step can be dismissed by clicking outside the panel.
  requestBackdropClose(): void {
    if (!this.canCloseFromBackdrop) {
      return;
    }

    this.requestClose();
  }

  // Shared close handling: loading and complete screens require explicit confirmation before teardown.
  requestClose(): void {
    if (this.closeConfirmationMessage && !window.confirm(this.closeConfirmationMessage)) {
      return;
    }

    this.closeModal();
  }

  private closeModal(): void {
    this.clearExportTimers();
    this.closed.emit();
  }

  // Config state: validate the selected export and register it before transitioning into loading.
  async confirmExportConfiguration(): Promise<void> {
    if (!this.exportType || this.selectedResourceCount === 0 || this.isRecaptchaVerifying) {
      return;
    }

    if (!this.orgConnectionId) {
      this.exportRequestErrorMessage = 'We could not determine which connection to export. Please try again.';
      return;
    }

    this.recaptchaState = 'verifying';
    this.recaptchaErrorMessage = '';
    this.exportRequestErrorMessage = '';

    try {
      this.recaptchaToken = await this.recaptchaService.execute('account_export_confirm');
    } catch (error) {
      console.error(error);
      this.recaptchaState = 'error';
      this.recaptchaErrorMessage = 'We could not complete the reCAPTCHA security check. Please try again.';
      return;
    }

    let exportResponse: VaultEhiExportResponse;

    try {
      exportResponse = await firstValueFrom(this.fastenService.registerVaultEhiExport(
        {
          org_connection_id: this.orgConnectionId,
          filters: {
            resource_types: this.exportResourceOptions
              .filter((option) => option.selected)
              .map((option) => option.resourceType),
          },
          output: {
            export_types: [this.exportType],
          },
        },
        this.recaptchaToken,
      ));
    } catch (error) {
      this.recaptchaState = 'pending';
      this.exportRequestErrorMessage = 'We could not register this export request. Please try again.';
      return;
    }

    if (exportResponse.status === 'failed') {
      this.recaptchaState = 'pending';
      this.exportRequestErrorMessage = this.getExportFailureMessage(exportResponse)
        || 'We could not register this export request. Please try again.';
      return;
    }

    this.startExportFlow(exportResponse);
  }

  // Config state interactions: keep the selection model isolated from loading and complete behavior.
  toggleExportResource(resourceType: string): void {
    this.exportResourceOptions = this.exportResourceOptions.map((option) => option.resourceType === resourceType
      ? { ...option, selected: !option.selected }
      : option);
  }

  selectAllResources(): void {
    this.exportResourceOptions = this.exportResourceOptions.map((option) => ({
      ...option,
      selected: true,
    }));
  }

  setExportDuration(durationId: ExportAccessDurationOption['id']): void {
    this.selectedExportDurationId = durationId;
  }

  trackByResourceType(_: number, option: ExportResourceOption): string {
    return option.resourceType;
  }

  trackByDurationId(_: number, option: ExportAccessDurationOption): string {
    return option.id;
  }

  // Closed -> config transition: opening always resets transient progress so each export starts cleanly.
  private prepareForOpen(): void {
    this.clearExportTimers();
    this.exportModalState = 'config';
    this.exportLoadingPhase = 'retrieving';
    this.lastStatusCheckLabel = '';
    this.retrievalCheckCount = 0;
    this.downloadUrl = '';
    this.resultDocumentUrl = '';
    this.qrCodeDataUrl = '';
    this.smartHealthLinkUrl = null;
    this.cardTitle = 'SMART Health Link';
    this.patientName = '';
    this.patientDob = '';
    this.verifiedDate = null;
    this.recaptchaState = 'pending';
    this.recaptchaErrorMessage = '';
    this.recaptchaToken = '';
    this.exportRequestErrorMessage = '';
    this.resetExportConfiguration();
  }

  // Closed state: tear down timers and clear any UI copied from an in-flight export.
  private resetClosedState(): void {
    this.clearExportTimers();
    this.exportModalState = 'closed';
    this.exportLoadingPhase = 'retrieving';
    this.lastStatusCheckLabel = '';
    this.retrievalCheckCount = 0;
    this.downloadUrl = '';
    this.resultDocumentUrl = '';
    this.qrCodeDataUrl = '';
    this.smartHealthLinkUrl = null;
    this.cardTitle = 'SMART Health Link';
    this.patientName = '';
    this.patientDob = '';
    this.verifiedDate = null;
    this.recaptchaState = 'pending';
    this.recaptchaErrorMessage = '';
    this.recaptchaToken = '';
    this.exportRequestErrorMessage = '';
  }

  private resetExportConfiguration(): void {
    this.exportResourceOptions = [
      { label: 'Allergies', resourceType: 'AllergyIntolerance', selected: true },
      { label: 'Conditions', resourceType: 'Condition', selected: true },
      { label: 'Immunizations', resourceType: 'Immunization', selected: true },
      { label: 'Vital Signs', resourceType: 'Observation', selected: true },
      { label: 'Care Plans', resourceType: 'CarePlan', selected: true },
      { label: 'Medications', resourceType: 'MedicationRequest', selected: true },
      { label: 'Procedures', resourceType: 'Procedure', selected: true },
      { label: 'Lab Results', resourceType: 'DiagnosticReport', selected: true },
      { label: 'Encounters', resourceType: 'Encounter', selected: true },
      { label: 'Clinical Notes', resourceType: 'DocumentReference', selected: true },
    ];
    this.selectedExportDurationId = '1-week';
  }

  // Loading state: once the backend request is registered, the modal advances through retrieval first.
  private startExportFlow(exportResponse: VaultEhiExportResponse): void {
    this.recaptchaState = 'pending';
    this.exportModalState = 'loading';
    this.exportLoadingPhase = 'retrieving';
    this.retrievalCheckCount = 0;
    this.lastStatusCheckLabel = this.formatStatusCheckLabel(new Date());
    this.downloadUrl = '';
    this.resultDocumentUrl = '';
    this.qrCodeDataUrl = '';
    this.smartHealthLinkUrl = null;
    this.cardTitle = 'SMART Health Link';
    this.patientName = '';
    this.patientDob = '';
    this.verifiedDate = null;

    if (!exportResponse.task_id) {
      this.handleExportPollingFailure('We could not start this export. Please try again.');
      return;
    }

    this.startRetrievalPolling(exportResponse.task_id);
  }

  // Loading state: poll the real export task endpoint until the export succeeds or fails.
  private startRetrievalPolling(taskId: string): void {
    this.clearExportTimers();

    this.retrievalPollingSubscription = timer(0, 5000).pipe(
      switchMap(() => {
        this.retrievalCheckCount += 1;
        this.lastStatusCheckLabel = this.formatStatusCheckLabel(new Date());
        return this.fastenService.getVaultEhiExport(taskId);
      }),
    ).subscribe({
      next: (response: VaultEhiExportResponse) => {
        if (response.status === 'success') {
          void this.completeExportFlow(taskId, response);
          return;
        }

        if (response.status === 'failed') {
          this.handleExportPollingFailure(
            this.getExportFailureMessage(response) || 'We could not complete this export. Please try again.',
          );
        }
      },
      error: () => {
        this.handleExportPollingFailure('We could not retrieve the export status. Please try again.');
      },
    });
  }

  // Loading -> complete transition: PDF exports expose a download URL, while link-based exports render the QR result.
  private async completeExportFlow(taskId: string, exportResponse: VaultEhiExportResponse): Promise<void> {
    this.clearExportTimers();

    if (this.exportType === 'pdf') {
      this.exportModalState = 'complete';
      this.downloadUrl = this.resolveExportResultUrl(exportResponse);
      this.resultDocumentUrl = '';
      return;
    }

    if (!this.exportType) {
      this.handleExportPollingFailure('We could not determine which export to finish. Please try again.');
      return;
    }

    this.exportLoadingPhase = 'qr-generation';

    try {
      const payload = await firstValueFrom(this.fastenService.createShlinkManifest({
        task_id: taskId,
        export_type: this.exportType,
        duration: this.selectedExportDurationId,
      }));

      if (payload.status === 'failed') {
        this.handleExportPollingFailure(
          this.getManifestFailureMessage(payload) || 'We could not create the SMART Health Link. Please try again.',
        );
        return;
      }

      this.smartHealthLinkUrl = payload.shlink || null;
      this.cardTitle = payload.card_title || 'SMART Health Link';
      this.patientName = payload.patient_name || '';
      this.patientDob = payload.patient_dob || '';
      this.verifiedDate = payload.verified_at || null;

      if (!this.smartHealthLinkUrl) {
        this.handleExportPollingFailure('No link was returned from the server.');
        return;
      }

      try {
        this.qrCodeDataUrl = await QRCode.toDataURL(this.smartHealthLinkUrl, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 260,
        });
      } catch (error) {
        console.error('Failed to generate QR code', error);
        this.handleExportPollingFailure('Unable to generate QR code. Please try again.');
        return;
      }

      this.resultDocumentUrl = 'https://viewer.tcpdev.org/#' + this.smartHealthLinkUrl;
      this.exportModalState = 'complete';
    } catch (error) {
      console.error('Failed to create shlink manifest', error);
      this.handleExportPollingFailure('We could not create the SMART Health Link. Please try again.');
    }
  }

  // Shared timer cleanup: both close/reset paths and loading transitions reuse the same teardown.
  private clearExportTimers(): void {
    if (this.retrievalPollingSubscription) {
      this.retrievalPollingSubscription.unsubscribe();
      this.retrievalPollingSubscription = null;
    }
  }

  private getSelectedDurationLabel(): string {
    return this.exportDurationOptions.find((option) => option.id === this.selectedExportDurationId)?.label || '1 week';
  }

  private formatStatusCheckLabel(timestamp: Date): string {
    return timestamp.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  // Shared presentation helpers: the modal derives UI copy from the export type instead of a custom action object.
  private getExportTypeLabel(): string {
    if (this.exportType === 'cms_patient_shared_health_document') {
      return 'Export as CMS Patient Document';
    }

    if (this.exportType === 'fhir_bundle') {
      return 'Export as Smart Health Link';
    }

    if (this.exportType === 'pdf') {
      return 'Export as PDF';
    }

    return 'Account export';
  }

  private buildMockDownloadUrl(): string {
    const selectedResources = this.exportResourceOptions
      .filter((option) => option.selected)
      .map((option) => option.resourceType)
      .join(', ');
    const fileContents = [
      `Mock export type: ${this.getExportTypeLabel()}`,
      `Connected account: ${this.accountDisplayName}`,
      `Selected resources: ${selectedResources}`,
    ].join('\n');

    return `data:text/plain;charset=utf-8,${encodeURIComponent(fileContents)}`;
  }

  private resolveExportResultUrl(exportResponse: VaultEhiExportResponse): string {
    const matchingDownloadLink = exportResponse.download_links?.find((downloadLink) =>
      downloadLink?.export_type === this.exportType
        && typeof downloadLink.url === 'string'
        && downloadLink.url.length > 0,
    )?.url;

    if (matchingDownloadLink) {
      return matchingDownloadLink;
    }

    const responseUrl = [
      exportResponse.download_url,
      exportResponse.content_url,
      exportResponse['share_url'],
      exportResponse['url'],
    ].find((candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0);

    if (responseUrl) {
      return responseUrl;
    }

    return this.exportType === 'pdf'
      ? this.buildMockDownloadUrl()
      : 'https://example.org/mock-patient-export';
  }

  private handleExportPollingFailure(message: string): void {
    this.clearExportTimers();
    this.recaptchaState = 'pending';
    this.exportModalState = 'config';
    this.exportLoadingPhase = 'retrieving';
    this.downloadUrl = '';
    this.resultDocumentUrl = '';
    this.qrCodeDataUrl = '';
    this.smartHealthLinkUrl = null;
    this.cardTitle = 'SMART Health Link';
    this.patientName = '';
    this.patientDob = '';
    this.verifiedDate = null;
    this.exportRequestErrorMessage = message;
  }

  private getExportFailureMessage(exportResponse: VaultEhiExportResponse): string {
    const messageParts = [
      exportResponse.error_description,
      exportResponse.error,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);

    return messageParts.join(' ');
  }

  private getManifestFailureMessage(response: {error?: string; error_description?: string}): string {
    const messageParts = [
      response.error_description,
      response.error,
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);

    return messageParts.join(' ');
  }
}
